import type { OperationSearchItem, ProductionBatch } from '@interfaces/search';
import { getFirstLinkWithPart, joinTraceSubtitle, notEmpty, uniqueById } from './traceabilityGraphUtils';
import { LinkItem, MachineItem, TraceabilityEntitySummary } from '@v2/pages/common/components/TraceabilityGraph/types';

interface BuildPartParams {
  links: LinkItem[];
  partId: number | null;
  partNumber: string | null;
  partDescription: string | null;
  partImageHash: string | null;
  partImageDocumentId: number | null;
}

export const buildPart = ({
  links,
  partId,
  partNumber,
  partDescription,
  partImageHash,
  partImageDocumentId,
}: BuildPartParams): TraceabilityEntitySummary | null => {
  const link = getFirstLinkWithPart(links);
  const resolvedPartId = partId ?? link?.partId;

  if (resolvedPartId == null) return null;

  return {
    id: resolvedPartId,
    title: partNumber ?? link?.partName ?? `#${resolvedPartId}`,
    subtitle: partDescription,
    imageHash: partImageHash,
    imageDocumentId: partImageDocumentId,
  };
};

export const buildProductionOrders = ({ links, jobs }: { links: LinkItem[]; jobs: OperationSearchItem[] }): TraceabilityEntitySummary[] => {
  const fromLinks = links
    .map((link) =>
      link?.productionOrderId == null
        ? null
        : {
            id: link.productionOrderId,
            title: link.productionOrderNumber ?? `#${link.productionOrderId}`,
            subtitle: link.partName ? `Part ${link.partName}` : link.linkType,
          }
    )
    .filter(notEmpty);

  const fromJobs = jobs
    .map((job) =>
      job?.productionOrderId == null
        ? null
        : {
            id: job.productionOrderId,
            title: job.productionOrderNumber ?? `#${job.productionOrderId}`,
            subtitle: job.partNumber ? `Part ${job.partNumber}` : job.status,
            imageHash: job.productionOrderImageHash,
            imageDocumentId: job.productionOrderImageDocumentId,
          }
    )
    .filter(notEmpty);

  return uniqueById([...fromLinks, ...fromJobs]);
};

export const buildJobSummaries = ({ jobs, links }: { jobs: OperationSearchItem[]; links: LinkItem[] }): TraceabilityEntitySummary[] => {
  const fromJobs = jobs
    .map((job) =>
      job?.productionOperationId == null
        ? null
        : {
            id: job.productionOperationId,
            title: job.productionOperationNumber ?? job.operationName ?? `#${job.productionOperationId}`,
            subtitle: job.productionOrderNumber ? `Order ${job.productionOrderNumber}` : job.status,
            imageHash: job.imageHash,
            imageDocumentId: job.imageDocumentId,
          }
    )
    .filter(notEmpty);

  const fromLinks = links
    .map((link) =>
      link?.productionOperationId == null
        ? null
        : {
            id: link.productionOperationId,
            title: link.productionOperationNumber ?? `#${link.productionOperationId}`,
            subtitle: link.productionOrderNumber ? `Order ${link.productionOrderNumber}` : undefined,
          }
    )
    .filter(notEmpty);

  return uniqueById([...fromJobs, ...fromLinks]);
};

export const buildBatchSummaries = (batches: ProductionBatch[]): TraceabilityEntitySummary[] =>
  batches
    .map((batch) => {
      if (batch?.productionBatchId == null) return null;

      const productionLink = batch.links?.find((link) => link?.productionOperationNumber);

      return {
        id: batch.productionBatchId,
        title: batch.batchNumber ?? `#${batch.productionBatchId}`,
        subtitle: joinTraceSubtitle([
          productionLink?.productionOperationNumber ? `Job ${productionLink.productionOperationNumber}` : null,
          batch.machineShortName,
        ]),
        imageHash: batch.imageHash,
        imageDocumentId: batch.imageDocumentId,
        ncFileStatus: batch.ncFileStatus,
      };
    })
    .filter(notEmpty);

interface BuildMachinesParams {
  batches: ProductionBatch[];
  jobs: OperationSearchItem[];
  firstRanByMachineId: number | null;
  firstRanByMachineName: string | null;
  lastRanByMachineId: number | null;
  lastRanByMachineName: string | null;
}

export const buildMachines = ({
  batches,
  jobs,
  firstRanByMachineId,
  firstRanByMachineName,
  lastRanByMachineId,
  lastRanByMachineName,
}: BuildMachinesParams): TraceabilityEntitySummary[] => {
  // Run machines first so their "First/Last ran" subtitle wins the de-dup over batch/job entries.
  const firstRan =
    firstRanByMachineId == null
      ? null
      : {
          id: firstRanByMachineId,
          title: firstRanByMachineName ?? `#${firstRanByMachineId}`,
          subtitle: firstRanByMachineId === lastRanByMachineId ? 'First & last ran' : 'First ran',
        };

  const lastRan =
    lastRanByMachineId == null || lastRanByMachineId === firstRanByMachineId
      ? null
      : {
          id: lastRanByMachineId,
          title: lastRanByMachineName ?? `#${lastRanByMachineId}`,
          subtitle: 'Last ran',
        };

  const fromBatches = batches
    .map((batch) => {
      if (batch?.machineId == null) return null;

      return {
        id: batch.machineId,
        title: batch.machineShortName ?? batch.machineFullName ?? `#${batch.machineId}`,
        subtitle: batch.batchNumber ? `Batch ${batch.batchNumber}` : undefined,
      };
    })
    .filter(notEmpty);

  const fromJobs = jobs.flatMap((job) =>
    (job?.machines ?? [])
      .map((machine: MachineItem) =>
        machine.machineId == null
          ? null
          : {
              id: machine.machineId,
              title: machine.machineName ?? `#${machine.machineId}`,
              subtitle: machine.linkType ?? (job.productionOperationNumber ? `Job ${job.productionOperationNumber}` : undefined),
            }
      )
      .filter(notEmpty)
  );

  return uniqueById([firstRan, lastRan, ...fromBatches, ...fromJobs].filter(notEmpty));
};
